import { __read, __assign } from 'tslib';
import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { getCookie, setCookie } from 'react-use-cookie';
import { WebAuth } from 'auth0-js';

function createAuth0Client(config) {
    var domain = config.domain, clientID = config.clientID, directoryHostname = config.directoryHostname;
    var _a = window.location, protocol = _a.protocol, port = _a.port;
    var redirectUri = "".concat(protocol, "//").concat(directoryHostname).concat(port ? ":".concat(port) : '', "/auth");
    return new WebAuth({
        domain: domain,
        clientID: clientID,
        redirectUri: redirectUri,
        responseType: 'id_token',
        scope: 'openid https://graphcms.com/loginsCount',
    });
}

function getLogoutURL(directoryHostName, studioHostName, next) {
    if (next === void 0) { next = location.href; }
    var nextUrl = btoa(next);
    var _a = window.location, protocol = _a.protocol, port = _a.port;
    var loginPage = "".concat(protocol, "//").concat(directoryHostName).concat(port ? ":".concat(port) : '', "/login?next=").concat(nextUrl);
    return {
        loginPage: loginPage,
        studioLogoutURL: studioHostName ? "".concat(protocol, "//").concat(studioHostName, "/logout") : loginPage,
    };
}

var AuthClientContext = createContext(undefined);
function useAuthClient() {
    var auth = useContext(AuthClientContext);
    if (typeof auth === 'undefined') {
        throw 'AuthClientContext missing, make sure to be a children of AuthClientProvider';
    }
    return auth;
}
var isLocal = process.env.NODE_ENV === 'development';
var cookieOptions = {
    SameSite: 'Lax',
    Secure: !isLocal,
    path: '/',
};
var AuthClientProvider = function (_a) {
    var children = _a.children, _b = _a.config, cookieDomain = _b.cookieDomain, _c = _b.cookieName, cookieName = _c === void 0 ? 'managementApiToken' : _c, auth0Domain = _b.auth0Domain, auth0ClientId = _b.auth0ClientId, directoryHostname = _b.directoryHostname, studioLogoutHostname = _b.studioLogoutHostname, _d = _b.authStorageKey, authStorageKey = _d === void 0 ? 'gcmsauthprovider' : _d, _e = _b.tokenStorageKey, tokenStorageKey = _e === void 0 ? 'managementApiToken' : _e;
    var _f = __read(useState(getCookie(cookieName)), 2), token = _f[0], setToken = _f[1];
    var client = useMemo(function () {
        return createAuth0Client({
            clientID: auth0ClientId,
            domain: auth0Domain,
            directoryHostname: directoryHostname,
        });
    }, [auth0ClientId, auth0Domain, directoryHostname]);
    var initiateLogin = useCallback(function (next) {
        // clear existing auth token
        setCookie(tokenStorageKey, '', __assign({ domain: cookieDomain, days: 0 }, cookieOptions));
        var state = Date.now().toString();
        setCookie(authStorageKey, JSON.stringify({ state: state, next: next }), __assign({ days: 1 / 96, domain: cookieDomain }, cookieOptions));
        return client.authorize({ state: state, mode: 'login' });
    }, [authStorageKey, client]);
    var initiateSignup = useCallback(
    // @ts-expect-error the types say that initial_screen does not exist (anymore), but it is still the only reliable way to display the register form instead of the login form
    function () { return client.authorize({ mode: 'signUp', initial_screen: 'signUp' }); }, [client]);
    var getNextUrl = useCallback(function (state) {
        var item = getCookie(authStorageKey);
        if (item) {
            var next = JSON.parse(item);
            if (next.state && state === next.state) {
                return next.next;
            }
        }
        return null;
    }, [authStorageKey]);
    var login = useCallback(function (token) {
        setCookie(tokenStorageKey, token, __assign({ domain: cookieDomain, days: 30 }, cookieOptions));
        setToken(token);
    }, [setCookie]);
    var logout = useCallback(function (options) {
        var _a = options || {}, logoutURL = _a.logoutURL, logoutFromStudio = _a.logoutFromStudio;
        var _b = logoutURL ?
            { loginPage: logoutURL, studioLogoutURL: logoutURL } :
            getLogoutURL(directoryHostname, studioLogoutHostname), loginPage = _b.loginPage, studioLogoutURL = _b.studioLogoutURL;
        var returnTo = logoutFromStudio ? studioLogoutURL : loginPage;
        setCookie(cookieName, '', __assign({ domain: cookieDomain, days: 0 }, cookieOptions));
        setToken('');
        try {
            client.logout({
                returnTo: returnTo,
            });
        }
        catch (_c) {
            window.location.assign(loginPage);
        }
    }, []);
    return (React.createElement(AuthClientContext.Provider, { value: {
            initiateSignup: initiateSignup,
            initiateLogin: initiateLogin,
            getNextUrl: getNextUrl,
            login: login,
            client: client,
            logout: logout,
            token: token,
        }, children: children }));
};

export { AuthClientProvider, useAuthClient };
//# sourceMappingURL=index.esm.js.map
